/**
 * Analytics Consent Manager
 *
 * This component manages PostHog analytics consent state.
 * It syncs the user's consent preference from the database to PostHog.
 *
 * - If consent is 'accepted', enables PostHog tracking
 * - If consent is 'rejected' or 'pending', disables PostHog tracking
 * - Respects the browser's Do Not Track setting
 */

import { useEffect } from 'react';
import posthog from 'posthog-js';
import { useConsentStatus } from '@have-done/shared';

export function AnalyticsConsent() {
  const consentStatus = useConsentStatus();

  useEffect(() => {
    // Wait for consent status to load
    if (consentStatus === undefined) return;

    // Check browser's Do Not Track setting
    const doNotTrack =
      navigator.doNotTrack === '1' ||
      (window as unknown as { doNotTrack?: string }).doNotTrack === '1';

    if (doNotTrack) {
      // Respect DNT - always opt out
      posthog.opt_out_capturing();
      return;
    }

    // If user is not logged in, keep tracking disabled
    if (consentStatus === null) {
      posthog.opt_out_capturing();
      return;
    }

    // Sync consent state with PostHog
    if (consentStatus.analytics === 'accepted') {
      posthog.opt_in_capturing();

      // Capture page view now that we have consent
      posthog.capture('$pageview');
    } else {
      // Either 'rejected' or 'pending' - no tracking
      posthog.opt_out_capturing();
    }
  }, [consentStatus]);

  // This component doesn't render anything
  return null;
}
